import { z } from 'zod';
import type { StatusChipVariant } from '@/components/ui/status-chip';
import type { EmailMode, Job, JobReport, JobRequest, JobStatus, JobType } from '@/services/api/mm-program-jobs';
import { parseStrictUtcDate, utcDayMs } from '@/utils/date';
import { formatUtcMonth } from '@/utils/format';
import { addDays, todayUtc } from '../date-bounds';

export type RequestType = JobRequest['type'];

export const REQUEST_TYPES = ['score', 'daily_report', 'monthly_report'] as const satisfies readonly RequestType[];
export const EMAIL_MODES = ['skip', 'default', 'override'] as const satisfies readonly EmailMode[];

export const RUN_HOURS = ['00', '06', '12', '18'] as const;
export type RunHour = (typeof RUN_HOURS)[number];

const RUN_MS = 6 * 60 * 60 * 1000;
// Overrides only reach One Trading; bop-api and the job refuse other addresses as well.
const ALLOWED_DOMAIN = 'onetrading.com';

export const JOB_TYPE_LABELS: Record<JobType, string> = {
  scheduled: 'Scheduled run',
  score: 'Backfill',
  daily_report: 'Daily report',
  monthly_report: 'Monthly report',
};

const jobFormFields = z.object({
  type: z.enum(REQUEST_TYPES),
  /** First and last run of a backfill, both included */
  firstDay: z.string(),
  firstRun: z.enum(RUN_HOURS),
  lastDay: z.string(),
  lastRun: z.enum(RUN_HOURS),
  date: z.string(),
  month: z.string(),
  /** A monthly report's first UTC day, `YYYY-MM-DD` in `month`; blank reports the whole month */
  fromDay: z.string(),
  emailMode: z.enum([...EMAIL_MODES, '']),
  emailTo: z.string(),
  /** Addresses separated by commas or spaces */
  bcc: z.string(),
  marketMakers: z.array(z.string()),
  resend: z.boolean(),
  /** A backfill's reports, sent after its runs when their day or month is among them */
  dailyReports: z.boolean(),
  monthlyReports: z.boolean(),
});

export type JobFormState = z.infer<typeof jobFormFields>;

const runStart = (day: string, run: RunHour) => Date.parse(`${day}T${run}:00:00Z`);
const iso = (ms: number) => `${new Date(ms).toISOString().slice(0, 19)}Z`;

/** The last 6-hour run that has completed: the latest a backfill can score. */
export const lastCompletedRun = (now: Date): { day: string; run: RunHour } => {
  const start = iso(Math.floor(now.getTime() / RUN_MS) * RUN_MS - RUN_MS);
  return { day: start.slice(0, 10), run: start.slice(11, 13) as RunHour };
};

/** The last UTC day that has ended, `YYYY-MM-DD`: the latest a daily report can cover. */
export const lastEndedDay = (now: Date) => addDays(todayUtc(now), -1);

/** The last UTC month that has ended, `YYYY-MM`: the latest a monthly report can cover. */
export const lastEndedMonth = (now: Date) => new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 1, 1)).toISOString().slice(0, 7);

/** Yesterday, the last day that has surely ended, and last month. */
export const emptyJobForm = (now: Date): JobFormState => {
  const yesterday = lastEndedDay(now);
  const lastMonth = lastEndedMonth(now);
  return {
    type: 'score',
    firstDay: yesterday,
    firstRun: '00',
    lastDay: yesterday,
    lastRun: '18',
    date: yesterday,
    month: lastMonth,
    fromDay: '',
    emailMode: '',
    emailTo: '',
    bcc: '',
    marketMakers: [],
    resend: false,
    dailyReports: false,
    monthlyReports: false,
  };
};

/** A month's first and last UTC day, both included. */
export const monthDays = (month: string): { first: string; last: string } => {
  const first = `${month}-01`;
  const [year, monthIndex] = month.split('-').map(Number);
  return { first, last: new Date(Date.UTC(year, monthIndex, 0)).toISOString().slice(0, 10) };
};

const isDayOf = (day: string, month: string) => parseStrictUtcDate(day) !== null && day.slice(0, 7) === month;

/** A new month, and the start day while it still falls in it. */
export const withMonth = (state: Pick<JobFormState, 'fromDay'>, month: string): Pick<JobFormState, 'month' | 'fromDay'> => ({
  month,
  fromDay: isDayOf(state.fromDay, month) ? state.fromDay : '',
});

export const parseAddresses = (text: string): string[] => text.split(/[\s,;]+/).filter(Boolean);

const internal = (address: string) => /^[^@\s]+@[^@\s]+$/.test(address) && address.toLowerCase().endsWith(`@${ALLOWED_DOMAIN}`);

/** Days a backfill closes: those whose 18:00 run, the day's last, it scores. */
export const backfillReportDays = (state: JobFormState): string[] => {
  if (state.type !== 'score') return [];
  const first = runStart(state.firstDay, state.firstRun);
  const last = runStart(state.lastDay, state.lastRun);
  if (!Number.isFinite(first) || !Number.isFinite(last) || last < first) return [];
  const days: string[] = [];
  for (let day = state.firstDay; runStart(day, '18') <= last; day = addDays(day, 1)) {
    if (runStart(day, '18') >= first) days.push(day);
  }
  return days;
};

/** Months a backfill closes: those whose last day's 18:00 run it scores. */
export const backfillReportMonths = (state: JobFormState): string[] =>
  backfillReportDays(state)
    .filter((day) => addDays(day, 1).endsWith('-01'))
    .map((day) => day.slice(0, 7));

/** The reports a backfill sends: those switched on whose day or month it closes. */
export const backfillReports = (state: JobFormState): JobReport[] => [
  ...(state.dailyReports && backfillReportDays(state).length ? (['daily'] as const) : []),
  ...(state.monthlyReports && backfillReportMonths(state).length ? (['monthly'] as const) : []),
];

const sendsDaily = (state: JobFormState) => state.type === 'daily_report' || backfillReports(state).includes('daily');
const sendsMonthly = (state: JobFormState) => state.type === 'monthly_report' || backfillReports(state).includes('monthly');

/** Whether the job sends any report, and so needs an email choice. A backfill without reports only rescores. */
export const sendsReports = (state: JobFormState) => sendsDaily(state) || sendsMonthly(state);

/** Daily reports go to each market maker with the bcc list it has set, so the job takes no bcc of its own. */
export const sendsToMarketMakers = (state: JobFormState) => state.emailMode === 'default' && sendsDaily(state);

/** Whether the job takes a bcc: an override, or the monthly report to One Trading's recipients. Nothing sent, no bcc. */
export const takesBcc = (state: JobFormState) => state.emailMode === 'override' || (state.emailMode === 'default' && !sendsDaily(state));

export const EMAIL_MODE_LABELS: Record<EmailMode, string> = {
  skip: 'Send nothing',
  default: 'Default email addresses',
  override: 'Override email addresses',
};

const outsideAddresses = (addresses: string[]) => {
  const outside = addresses.filter((address) => !internal(address));
  return outside.length ? `Only @${ALLOWED_DOMAIN} addresses: ${outside.join(', ')}` : null;
};

/**
 * The job form's values, and what to fix before asking bop-api, which checks everything again. Each problem sits on the
 * field it is about. `now` sets the latest run, day and month that can be picked.
 */
export const jobFormSchema = (now: () => Date = () => new Date()) =>
  jobFormFields.superRefine((state, context) => {
    const problem = (path: keyof JobFormState, message: string) => context.addIssue({ code: 'custom', path: [path], message });
    if (state.type === 'score') {
      const first = runStart(state.firstDay, state.firstRun);
      const last = runStart(state.lastDay, state.lastRun);
      const latest = lastCompletedRun(now());
      if (!Number.isFinite(first) || !Number.isFinite(last)) problem('lastDay', 'Choose the first and last run.');
      else if (last < first) problem('lastDay', 'The last run comes before the first.');
      else if (last > runStart(latest.day, latest.run)) problem('lastDay', `The last run can be ${latest.day} ${latest.run}:00 UTC at the latest: only completed runs are scored.`);
    }
    if (state.type === 'daily_report' && !state.date) problem('date', 'Choose the day.');
    else if (state.type === 'daily_report' && state.date > lastEndedDay(now()))
      problem('date', `The day can be ${lastEndedDay(now())} at the latest: only a day that has ended has a report.`);
    if (state.type === 'monthly_report' && !state.month) problem('month', 'Choose the month.');
    else if (state.type === 'monthly_report' && state.fromDay && !isDayOf(state.fromDay, state.month))
      problem('fromDay', `Choose a day in ${formatUtcMonth(utcDayMs(`${state.month}-01`))}, or leave it empty.`);
    // The email fields are hidden while a backfill sends no report, and left out of its request.
    if (!sendsReports(state)) return;
    if (!state.emailMode) problem('emailMode', 'Choose what happens to the email.');
    if (state.emailMode === 'override') {
      const to = state.emailTo.trim();
      const outside = to ? outsideAddresses([to]) : 'Enter the address to send to.';
      if (outside) problem('emailTo', outside);
    }
    // A hidden bcc or resend is left out of the request, so it is not checked either.
    const outsideBcc = takesBcc(state) ? outsideAddresses(parseAddresses(state.bcc)) : null;
    if (outsideBcc) problem('bcc', outsideBcc);
  });

/** Every problem with the form, for Queue job's hover. */
export const formProblems = (state: JobFormState, now = new Date()): string[] =>
  jobFormSchema(() => now)
    .safeParse(state)
    .error?.issues.map((issue) => issue.message) ?? [];

/** The request bop-api takes, from a form without problems. */
export const toJobRequest = (state: JobFormState, justification: string): JobRequest => {
  const request: JobRequest = { type: state.type, justification };
  if (state.type === 'score') {
    request.periods = [{ from: iso(runStart(state.firstDay, state.firstRun)), to: iso(runStart(state.lastDay, state.lastRun) + RUN_MS) }];
  }
  if (state.type === 'daily_report') request.date = state.date;
  if (state.type === 'monthly_report') request.month = state.month;
  if (state.type === 'monthly_report' && state.fromDay) request.from = state.fromDay;
  const reports = backfillReports(state);
  if (reports.length) request.reports = reports;
  if (!sendsReports(state)) return request;
  if (state.emailMode) {
    const bcc = takesBcc(state) ? parseAddresses(state.bcc) : [];
    request.email = {
      mode: state.emailMode,
      ...(state.emailMode === 'override' ? { to: state.emailTo.trim() } : {}),
      ...(bcc.length ? { bcc } : {}),
    };
  }
  if (state.marketMakers.length) request.market_makers = state.marketMakers;
  if (state.resend && sendsToMarketMakers(state)) request.resend = true;
  return request;
};

const runLabel = (run: string) => `${run.slice(0, 10)} ${run.slice(11, 16)}`;
/** A step of a job: a run start, or the day or month of a report. */
const stepLabel = (step: string) => (step.includes('T') ? runLabel(step) : step);

/** A month, and the day a partial monthly report starts on: `2026-10 from 2026-10-12`. */
export const describeMonth = (month: string, from?: string | null) => (from ? `${month} from ${from}` : month);

/** What a job covers: its runs, day or month. */
export const describeJobTarget = (job: Pick<Job, 'runs' | 'date' | 'month' | 'from'>): string => {
  if (job.runs?.length) {
    const first = job.runs[0];
    const last = job.runs[job.runs.length - 1];
    return job.runs.length === 1 ? runLabel(first) : `${job.runs.length} runs, ${runLabel(first)} to ${runLabel(last)}`;
  }
  if (job.month) return describeMonth(job.month, job.from);
  return job.date ?? '';
};

export const describeProgress = (job: Job): string => {
  const total = job.total ?? job.runs?.length ?? 1;
  const done = job.done ?? 0;
  if (job.status === 'queued') return 'Waiting for the instance';
  if (job.status === 'running') return `${done} of ${total} done${job.current ? `, at ${stepLabel(job.current)}` : ''}${job.cancel_requested ? ', stopping' : ''}`;
  if (job.status === 'failed') return `Failed${job.current ? ` at ${stepLabel(job.current)}` : ''}, ${done} of ${total} done`;
  if (job.status === 'cancelled') return `Cancelled, ${done} of ${total} done`;
  return `${done} of ${total} done`;
};

const STATUS_VARIANTS: Record<JobStatus, StatusChipVariant> = {
  queued: 'info',
  running: 'warning',
  succeeded: 'success',
  failed: 'destructive',
  cancelled: 'muted',
};

export const jobStatusVariant = (status: JobStatus): StatusChipVariant => STATUS_VARIANTS[status];

export const canCancel = (job: Job): boolean => (job.status === 'queued' || job.status === 'running') && !job.cancel_requested;
